import { useEffect, useState } from "react";
import api, { pctIt } from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, XCircle, History } from "lucide-react";
import { LongTextarea, NoteView } from "@/components/LongText";

const dIt = (s) => { if (!s) return ""; const [y, m, d] = s.slice(0, 10).split("-"); return `${d}/${m}/${y}`; };
const Esito = ({ v }) => v === true ? <span className="text-emerald-600 font-semibold">fatto</span> : v === false ? <span className="text-red-600 font-semibold">non fatto</span> : <span className="text-[var(--muted)]">da verificare</span>;

function Kpi({ k, v, sub, danger }) {
  return (
    <div className={`rounded-xl p-2.5 border ${danger ? "border-red-200 bg-red-50" : "border-[var(--border)] bg-[#F7F7F8]"}`}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--muted)]">{k}</div>
      <div className={`font-bold text-base ${danger ? "text-red-600" : ""}`}>{v}</div>
      {sub && <div className="text-[10px] text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

function Precedenti({ d, fatto, setFatto }) {
  const p = d.precedente;
  if (!p) return <p className="text-xs text-[var(--muted)]">Nessun impegno dalla volta scorsa.</p>;
  return (
    <div className="p-3 rounded-xl border border-[var(--border)]" data-testid="o2o-precedenti">
      <div className="text-xs text-[var(--muted)] mb-1">Settimana del {dIt(p.settimana)}</div>
      <div className="text-sm mb-2"><NoteView text={`"${p.impegni || "—"}"`} /> → <Esito v={fatto} /></div>
      {d.can_edit && (
        <div className="flex gap-2">
          <button data-testid="o2o-prec-fatto" onClick={() => setFatto(true)} className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border ${fatto === true ? "bg-emerald-500 text-white border-emerald-500" : "border-[var(--border)]"}`}><CheckCircle2 size={14} /> Fatto</button>
          <button data-testid="o2o-prec-non-fatto" onClick={() => setFatto(false)} className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border ${fatto === false ? "bg-red-500 text-white border-red-500" : "border-[var(--border)]"}`}><XCircle size={14} /> Non fatto</button>
        </div>
      )}
    </div>
  );
}

export default function OneToOneModal({ agentId, onClose }) {
  const [d, setD] = useState(null);
  const [imp, setImp] = useState("");
  const [fatto, setFatto] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/team/one-to-one/${agentId}`).then((r) => { setD(r.data); setImp(r.data.impegni_settimana || ""); setFatto(r.data.precedente?.fatto ?? null); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Accesso negato"); onClose(); });
  }, [agentId]); // eslint-disable-line
  const save = async () => {
    setSaving(true);
    try {
      const body = { impegni: imp };
      if (d.precedente) body.fatto = fatto;
      await api.post(`/team/one-to-one/${agentId}`, body); toast.success("One-to-one salvato"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  const s = d?.settimana || {};
  const ob = (r) => `${r?.fatto ?? 0}${r?.obiettivo ? ` / ${r.obiettivo}` : ""}`;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="o2o-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg card-immo p-5 rounded-t-3xl sm:rounded-3xl max-h-[92dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold" data-testid="o2o-title">One-to-one con {d?.agente || "…"}{d ? ` · lunedì ${dIt(d.lunedi)}` : ""}</h3>
          <button onClick={onClose} data-testid="o2o-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        {!d ? <p className="text-sm text-[var(--muted)]">Carico…</p> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-2" data-testid="o2o-kpi">
              <Kpi k="Valutazioni" v={ob(s.valutazioni)} sub={s.valutazioni?.obiettivo ? "obiettivo" : ""} danger={s.valutazioni?.obiettivo && s.valutazioni.fatto < s.valutazioni.obiettivo} />
              <Kpi k="Visite" v={ob(s.visite)} />
              <Kpi k="Proposte" v={ob(s.proposte)} />
              <Kpi k="Val. → incarico" v={pctIt(d.conversione_val_incarico)} sub={`media team ${pctIt(d.media_team)}`} danger={d.conversione_val_incarico < d.media_team} />
              <Kpi k="Esiti mancanti" v={d.esiti_mancanti} danger={d.esiti_mancanti > 0} />
              <Kpi k="Notizie ferme" v={d.notizie_ferme} danger={d.notizie_ferme > 0} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-1.5">Impegni della volta scorsa</div>
              <Precedenti d={d} fatto={fatto} setFatto={setFatto} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-1.5">Impegni di questa settimana</div>
              {d.can_edit
                ? <LongTextarea data-testid="o2o-impegni" value={imp} onChange={(e) => setImp(e.target.value)} rows={3} placeholder="Es. richiamare le 6 notizie di settembre" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
                : <NoteView testid="o2o-impegni-readonly" text={imp || "Il direttore non ha ancora fissato gli impegni di questa settimana."} lines={8} className="p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)]" />}
            </div>
            {d.storico?.length > 0 && (
              <div data-testid="o2o-storico">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-1.5 flex items-center gap-1"><History size={12} /> Settimane precedenti</div>
                <div className="space-y-1">{d.storico.map((h) => (
                  <div key={h.id} className="text-xs flex gap-2 justify-between border-b border-[var(--border)] py-1"><span className="text-[var(--muted)] shrink-0">{dIt(h.settimana)}</span><span className="flex-1 truncate">{h.impegni || "—"}</span><Esito v={h.fatto} /></div>
                ))}</div>
              </div>
            )}
            {d.can_edit && <button data-testid="o2o-salva" onClick={save} disabled={saving} className="w-full py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm disabled:opacity-50">{saving ? "Salvo…" : "Salva one-to-one"}</button>}
          </div>
        )}
      </div>
    </div>
  );
}
